import React from "react";
import { Timestamp } from "firebase/firestore";
import {
  Box,
  Typography,
  Paper,
  Grid2,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Rating,
  Card,
  CardContent,
  Divider,
  Avatar,
  styled,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ClearIcon from "@mui/icons-material/Clear";
import PrintIcon from "@mui/icons-material/Print";
import { printRatingReport } from "./ratingResultsPrint";
import { Branch } from "../../../interfaces/entities/branch";
import { User } from "../../../interfaces/entities/user";
import { Review } from "../../../interfaces/entities/review";

interface FilterState {
  selectedMonth: string;
  selectedYear: string;
  selectedEmployeeId: string;
  selectedStars: string;
}

interface RatingResultsProps {
  branch: Branch | null;
  companyId?: string;
  employees: User[];
  reviews: Review[];
  averageRating: number;
  totalReviews: number;
  filters: FilterState;
  onFilterChange: (name: keyof FilterState, value: string) => void;
  onClearFilters: () => void;
  topPerformer: User | null;
  topPerformerRating: number;
  topPerformerMonth: string;
  companyName: string;
  periodLabel: string;
  employeeSummary: { userId: string; empNo: string; name: string; averageRating: number; totalReviews: number }[];
}

export const RatingResults: React.FC<RatingResultsProps> = ({
  branch,
  employees,
  reviews,
  // averageRating,
  // totalReviews,
  filters,
  onFilterChange,
  onClearFilters,
  topPerformer,
  topPerformerRating,
  topPerformerMonth,
  companyName,
  periodLabel,
  employeeSummary,
}) => {
  const handlePrint = () => {
    const total = reviews.length;
    const avg = total > 0 ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / total : 0;
    printRatingReport({
      companyName,
      branchName: branch?.name ?? '',
      branchAddress: branch?.address,
      period: periodLabel,
      totalReviews: total,
      overallAverage: avg,
      rows: employeeSummary,
    });
  };
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const yearOptions = Array.from({ length: 5 }, (_, index) => String(new Date().getFullYear() - index));

  // const getRatingLabel = (value: number): string => {
  //   const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
  //   return labels[Math.round(value)] || "";
  // };

  const formatDate = (dateInput: Timestamp): string => {
    if (!dateInput) return "N/A";
    return dateInput.toDate().toDateString();
  };

  const getEmployeeName = (empId: string | undefined): string => {
    if (!empId) return "Unknown";
    const employee = employees.find((e) => e.id === empId);
    return employee ? `${employee.firstName} ${employee.lastName || ""}`.trim() : "Unknown";
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        Rating Results - {branch?.name || "Branch"}
      </Typography>

      {/* Average Rating Section */}
      <Paper elevation={2} sx={{ padding: 3, mb: 3, bgcolor: "#f8f9fa70" }}>
        <Grid2 container spacing={3} alignItems="center">
          <Grid2 container size={{ xs: 12, md: 4 }}>

            <Grid2 size={{ xs: 12, md: 4 }} display="flex"
              justifyContent="center"
              alignItems="center">
              <Grid2>
                <AvatarWrapper
                  src={topPerformer?.image || "/assets/default-user.jpg"}
                  alt={topPerformer ? `${topPerformer.firstName} ${topPerformer.lastName || ""}` : "Top Performer"}

                />
              </Grid2>

            </Grid2>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: "bold" }}>
                  {topPerformerRating > 0 ? topPerformerRating.toFixed(1) : "N/A"}
                </Typography>
                <Rating
                  value={topPerformerRating > 0 ? topPerformerRating : 0}
                  precision={0.1}
                  readOnly
                  size="large"
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderIcon fontSize="inherit" />}
                />
                {/* <Typography variant="body2" color="text.secondary">
                  {getRatingLabel(topPerformerRating)}
                </Typography> */}
                {topPerformer && (
                  <Typography variant="body1" sx={{ mt: 1, fontWeight: "bold" }}>
                    {topPerformer.firstName} {topPerformer.lastName || ""}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: -0.5 }}>
                  Top Performer of {topPerformerMonth}
                </Typography>
              </Box>
            </Grid2>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 8 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "medium" }}>
              Filter Ratings
            </Typography>

            <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={filters.selectedYear}
                    label="Year"
                    onChange={(e) => onFilterChange("selectedYear", e.target.value)}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={filters.selectedMonth}
                    label="Month"
                    disabled={!filters.selectedYear}
                    onChange={(e) => onFilterChange("selectedMonth", e.target.value)}
                  >
                    <MenuItem value="">All Months</MenuItem>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={filters.selectedEmployeeId}
                    label="Employee"
                    onChange={(e) => onFilterChange("selectedEmployeeId", e.target.value)}
                  >
                    <MenuItem value="">All Employees</MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.firstName} {employee.lastName || ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
            </Grid2>

            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rating</InputLabel>
                  <Select
                    value={filters.selectedStars}
                    label="Select Stars"
                    onChange={(e) => onFilterChange("selectedStars", e.target.value)}
                  >
                    <MenuItem value="">All Ratings</MenuItem>
                    <MenuItem value="5">5 Stars</MenuItem>
                    <MenuItem value="4">4 Stars</MenuItem>
                    <MenuItem value="3">3 Stars</MenuItem>
                    <MenuItem value="2">2 Stars</MenuItem>
                    <MenuItem value="1">1 Star</MenuItem>
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={onClearFilters}
                  fullWidth
                >
                  Clear
                </Button>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  fullWidth
                >
                  Print Report
                </Button>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>

      {/* Reviews List */}
      <Typography variant="h6" gutterBottom>
        Individual Reviews ({reviews.length})
      </Typography>

      {reviews.length === 0 ? (
        <Paper elevation={1} sx={{ padding: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No reviews found for the selected filters.
          </Typography>
        </Paper>
      ) : (
        <Grid2 container spacing={2}>
          {reviews.map((review) => (
            <Grid2 key={review.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card elevation={2} sx={{ bgcolor: "#f8f9fa70" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {getEmployeeName(review.userId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(review.updatedAt)}
                      </Typography>
                    </Box>
                    <Rating
                      value={review.rating || 0}
                      readOnly
                      size="small"
                      icon={<StarIcon fontSize="inherit" />}
                      emptyIcon={<StarBorderIcon fontSize="inherit" />}
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {review.comment && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {review.comment}
                    </Typography>
                  )}

                  {review.reviewerPhone && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Phone: {review.reviewerPhone}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {review.editCount && review.editCount > 0
                      ? `Edited ${review.editCount} time(s)`
                      : "-------"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      )}
    </Box>
  );
};

export const AvatarWrapper = styled(Avatar)({
  width: 130,
  height: 130,
  border: "3px solid white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
});